import React, { useEffect } from 'react';
import {
  Box,
  Dialog,
  IconButton,
  CircularProgress,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FlipCameraIosIcon from '@mui/icons-material/FlipCameraIos';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { useCameraCapture } from '../../hooks/useCameraCapture';

interface Props {
  open: boolean;
  onClose: () => void;
  onCapture: (file: File) => void;
  onError: (message: string) => void;
}

export const CommunityCameraModal: React.FC<Props> = ({
  open,
  onClose,
  onCapture,
  onError,
}) => {
  const {
    videoRef,
    isStarting,
    facingMode,
    startCamera,
    flipCamera,
    capturePhoto,
    stopCamera,
  } = useCameraCapture();

  useEffect(() => {
    if (!open) {
      stopCamera();
      return;
    }
    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    void startCamera(isMobile ? 'environment' : 'user').catch(() => {
      onError('Camera access denied');
      onClose();
    });
  }, [open, startCamera, stopCamera, onClose, onError]);

  const handleClose = () => {
    stopCamera();
    onClose();
  };

  const handleCapture = () => {
    const file = capturePhoto();
    if (!file) {
      onError('Could not capture photo');
      return;
    }
    stopCamera();
    onCapture(file);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen
      PaperProps={{ sx: { bgcolor: '#000' } }}
    >
      <Box sx={{ position: 'relative', width: '100%', height: '100%', bgcolor: '#000' }}>
        {isStarting && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2,
            }}
          >
            <CircularProgress sx={{ color: '#fff' }} />
          </Box>
        )}

        <Box
          component="video"
          ref={videoRef}
          autoPlay
          playsInline
          muted
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transform: facingMode === 'user' ? 'scaleX(-1)' : 'none',
          }}
        />

        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 1,
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.5), transparent)',
          }}
        >
          <IconButton onClick={handleClose} sx={{ color: '#fff' }} aria-label="Close camera">
            <CloseIcon />
          </IconButton>
          <Typography sx={{ color: '#fff', fontSize: '0.95rem', fontWeight: 500 }}>
            Camera
          </Typography>
          <IconButton
            onClick={() => void flipCamera().catch(() => onError('Could not switch camera'))}
            sx={{ color: '#fff' }}
            aria-label="Flip camera"
          >
            <FlipCameraIosIcon />
          </IconButton>
        </Box>

        <Box
          sx={{
            position: 'absolute',
            bottom: 32,
            left: 0,
            right: 0,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <IconButton
            onClick={handleCapture}
            disabled={isStarting}
            sx={{
              width: 72,
              height: 72,
              bgcolor: '#fff',
              border: '4px solid rgba(255,255,255,0.35)',
              '&:hover': { bgcolor: '#f0f0f0' },
            }}
            aria-label="Take photo"
          >
            <PhotoCameraIcon sx={{ fontSize: 32, color: '#111' }} />
          </IconButton>
        </Box>
      </Box>
    </Dialog>
  );
};
